import React, { Fragment, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { withStripe } from "./with_stripe";
import { lazyWithRetry } from "../core/lazy_with_retry";
import { useGetPartnerOffering } from "../data/api";
import { PartnerConfig, PaymentMethod } from "./partner_config";
import { TemplateOfferingCheckoutStripeDayPass } from "./template_offering_checkout_stripe_daypass";
import { PartnerOffering, Template } from "../data/partner_offering";
import { TemplateOfferingCheckoutStripeMultiPass } from "./template_offering_checkout_stripe_multipass";
import { TemplateOfferingCheckoutStripeDayPassWithCapacity } from "./template_offering_checkout_stripe_daypass_with_capacity";
import { TemplateOfferingCheckoutStripeMultiDayOffice } from "./template_multi_day_office/template_offering_checkout_stripe_multi_day_office";
import { TemplateOfferingCheckoutTranzilaDayOffice } from "./template_offering_checkout_tranzila_day_office";
import { TemplateOfferingCheckoutTranzilaDayPass } from "./template_offering_checkout_tranzila_daypass";
import { TemplateOfferingCheckoutTranzilaMultiDayOffice } from "./template_multi_day_office/template_offering_checkout_tranzila_multi_day_office";
import { TemplateOfferingCheckoutTranzilaMultiPass } from "./template_offering_checkout_tranzila_multipass";
import { TemplateOfferingCheckoutTranzilaDailyAccess } from "./template_offering_checkout_tranzila_daily_access";
import { OfferingMindspaceCheckoutMultiPass } from "./mindspace/offering_mindspace_checkout_multipass";
import { OfferingWeWorkCheckoutDayPass } from "./wework/offering_wework_checkout_daypass";
import { OfferingWeWorkCheckoutMultiPass } from "./wework/offering_wework_checkout_multipass";
import { OfferingWeWorkCheckoutDailyAccess } from "./wework/offering_wework_checkout_daily_access";
import { offeringConfig } from "./offering_config";
import { TemplateOfferingCheckoutStripeMeetingRoom } from "./template_offering_checkout_stripe_meeting_room";
import { TemplateOfferingCheckoutTranzilaMeetingRoom } from "./template_offering_checkout_tranzila_meeting_room";
import { OfferingWeWorkCheckoutDayOffice } from "./wework/offering_wework_checkout_day_office";
import { OfferingWeWorkMeetingRoomCheckout } from "./wework/offering_wework_checkout_meeting_room";
import OfferingCheckoutPageV2 from "../offering_v2/pages/offering_checkout";
import { useLocation } from "react-router";
import { useAnalytics } from "../lib/analytics/analytics";
import { TemplateOfferingCheckoutStripeDailyAccess } from "./template_offering_checkout_stripe_daily_access";
import { DiscountPopupProvider } from "@/core/discount_popup_provider";

interface OfferingCheckoutPageParams {
  offeringID: string;
}

export const OfferingCheckoutPage = withStripe(function OfferingCheckoutPage() {
  const pageParams = useParams<OfferingCheckoutPageParams>();
  const partnerOfferingSlug = pageParams.offeringID;

  const partnerOffering = useGetPartnerOffering(partnerOfferingSlug);

  const partnerSlug = partnerOffering.partner.partnerSlug;

  const [partnerConfig, setPartnerConfig] = useState<any>();
  import(`./${partnerSlug}/${partnerSlug}_partner_config.tsx`).then(
    (partner) => {
      setPartnerConfig(partner.partnerConfig);
    }
  );

  if (!partnerConfig) {
    return null;
  }

  /**
   * This config to return template checkout v2
   */
  const isOfferingTemplateV2 = [
    Template.DailyAccessV2.toString(),
    Template.DailyOffice.toString(),
    Template.MeetingRoomV2.toString(),
    Template.AddOnV2.toString(),
    Template.Membership,
    Template.EventSpace,
    Template.RoomGallery,
  ].includes(partnerOffering.template);
  return (
    <DiscountPopupProvider partnerId={partnerOffering.partner.id}>
      {isOfferingTemplateV2 ? (
        <OfferingCheckoutPageV2 partnerOffering={partnerOffering} />
      ) : (
        <OfferingCheckoutPageV1
          partnerOffering={partnerOffering}
          partnerConfig={partnerConfig}
        />
      )}
      <CheckoutPageTracker partnerOffering={partnerOffering} />
    </DiscountPopupProvider>
  );
});

function OfferingCheckoutPageV1(props: {
  partnerConfig: PartnerConfig;
  partnerOffering: PartnerOffering;
}) {
  const { partnerConfig, partnerOffering } = props;
  const partnerSlug = partnerOffering.partner.partnerSlug;
  const partnerOfferingSlug = partnerOffering.slug;

  const Theme = lazyWithRetry(
    () => import(`./${partnerSlug}/${partnerSlug}_theme.tsx`)
  );
  const Analytics = lazyWithRetry(
    () => import(`./${partnerSlug}/${partnerSlug}_analytics.tsx`)
  );

  /**
   * For this specific offering templates, we will keep the their own template offering
   */
  if (partnerOfferingSlug === offeringConfig.Mindspace.slugConfig.multiPass) {
    return <OfferingMindspaceCheckoutMultiPass />;
  } else if (partnerOfferingSlug === offeringConfig.WeWork.slugConfig.dayPass) {
    return <OfferingWeWorkCheckoutDayPass />;
  } else if (
    partnerOfferingSlug === offeringConfig.WeWork.slugConfig.multiPass
  ) {
    return <OfferingWeWorkCheckoutMultiPass />;
  } else if (
    partnerOfferingSlug === offeringConfig.WeWork.slugConfig.dailyAccess
  ) {
    return <OfferingWeWorkCheckoutDailyAccess />;
  } else if (
    partnerOfferingSlug === offeringConfig.WeWork.slugConfig.meetingRoom
  ) {
    return <OfferingWeWorkMeetingRoomCheckout />;
  } else if (
    partnerOfferingSlug === offeringConfig.WeWork.slugConfig.classroom
  ) {
    return <OfferingWeWorkMeetingRoomCheckout />;
  } else if (
    partnerOfferingSlug === offeringConfig.WeWork.slugConfig.dayOffice
  ) {
    return <OfferingWeWorkCheckoutDayOffice />;
  }
  const templateOffering = getCheckoutTemplate(
    partnerOffering.template,
    partnerConfig
  );

  if (!templateOffering) {
    return null;
  }

  return (
    <Fragment>
      <Theme />
      <Analytics />
      {templateOffering}
    </Fragment>
  );
}

function CheckoutPageTracker(props: { partnerOffering: PartnerOffering }) {
  const analytics = useAnalytics();
  const { pathname: pathName } = useLocation();
  const { template, partner } = props.partnerOffering;
  const { id, name, internalName } = partner;

  useEffect(() => {
    analytics.screen(
      "Checkout Page View",
      {
        partnerId: id,
        partnerName: name,
        partnerInternalName: internalName,
        pageName: `Checkout`,
        template: template,
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  }, [analytics, pathName, id, name, template, internalName]);

  return <Fragment />;
}

function getCheckoutTemplate(template: string, partnerConfig: PartnerConfig) {
  const { payment = PaymentMethod.Stripe } = partnerConfig;
  if (payment === PaymentMethod.Stripe) {
    return getStripeCheckoutTemplate(template, partnerConfig);
  } else {
    return getTranzilaCheckoutTemplate(template, partnerConfig);
  }
}

function getStripeCheckoutTemplate(
  template: string,
  partnerConfig: PartnerConfig
) {
  switch (template) {
    case Template.DayPass:
      return (
        <TemplateOfferingCheckoutStripeDayPass partnerConfig={partnerConfig} />
      );
    case Template.MultiPass:
      return (
        <TemplateOfferingCheckoutStripeMultiPass
          partnerConfig={partnerConfig}
        />
      );
    case Template.AddOn:
      return (
        <TemplateOfferingCheckoutStripeMultiPass
          partnerConfig={partnerConfig}
        />
      );
    case Template.DailyAccess:
      return (
        <TemplateOfferingCheckoutStripeDailyAccess
          partnerConfig={partnerConfig}
        />
      );
    case Template.DayOffice:
      return (
        <TemplateOfferingCheckoutStripeDayPassWithCapacity
          partnerConfig={partnerConfig}
        />
      );
    case Template.DayOfficeMultiPass:
      return (
        <TemplateOfferingCheckoutStripeMultiDayOffice
          partnerConfig={partnerConfig}
        />
      );
    case Template.MeetingRoom:
      return (
        <TemplateOfferingCheckoutStripeMeetingRoom
          partnerConfig={partnerConfig}
        />
      );
    case Template.SnowflakeDayOffice:
      throw new Error(
        "This checkout template: SnowflakeDayOffice by Stripe is not support"
      );
    default:
      throw new Error("This checkout template is not support" + template);
  }
}

function getTranzilaCheckoutTemplate(
  template: string,
  partnerConfig: PartnerConfig
) {
  switch (template) {
    case Template.DayPass:
      return (
        <TemplateOfferingCheckoutTranzilaDayPass
          partnerConfig={partnerConfig}
        />
      );
    case Template.MultiPass:
      return (
        <TemplateOfferingCheckoutTranzilaMultiPass
          partnerConfig={partnerConfig}
        />
      );
    case Template.AddOn:
      return (
        <TemplateOfferingCheckoutTranzilaMultiPass
          partnerConfig={partnerConfig}
        />
      );
    case Template.DailyAccess:
      return (
        <TemplateOfferingCheckoutTranzilaDailyAccess
          partnerConfig={partnerConfig}
        />
      );
    case Template.DayOffice:
      return (
        <TemplateOfferingCheckoutTranzilaDayOffice
          partnerConfig={partnerConfig}
        />
      );
    case Template.DayOfficeMultiPass:
      return (
        <TemplateOfferingCheckoutTranzilaMultiDayOffice
          partnerConfig={partnerConfig}
        />
      );
    case Template.MeetingRoom:
      return (
        <TemplateOfferingCheckoutTranzilaMeetingRoom
          partnerConfig={partnerConfig}
        />
      );
    case Template.SnowflakeDayOffice:
      return (
        <TemplateOfferingCheckoutTranzilaDayOffice
          partnerConfig={partnerConfig}
        />
      );
    default:
      throw new Error("This checkout template is not support" + template);
  }
}
